import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';

// Import the Add/Modify Tip component
import EL_AddORModifyTip from './EL_AddORModifyTip';

const EL_ManageWeeklyTips: React.FC = () => {
    
  const API_BASE =
    process.env.REACT_APP_DOCUMENT_LINK;

    // --- State ---
    const [countries, setCountries] = useState<string[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    
    const [generalTips, setGeneralTips] = useState<any[]>([]);
    const [altTips, setAltTips] = useState<any[]>([]);
    
    const [selectedGenTip, setSelectedGenTip] = useState<number | null>(null);
    const [selectedAltTip, setSelectedAltTip] = useState<number | null>(null);
    
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string>('');

    // --- Modal State ---
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalAction, setModalAction] = useState<'A' | 'R' | 'G'>('A');
    const [modalTipType, setModalTipType] = useState<'GeneralTip' | 'AlternateTip'>('AlternateTip');
    const [modalUniqueId, setModalUniqueId] = useState<number | null>(null);


    document.body.style.backgroundColor="#fff";

    // --- Initialization ---
    useEffect(() => {
        document.body.style.margin = '0px';
        document.body.style.fontFamily = 'Arial, Trebuchet MS, sans-serif';
        const init = async () => {
            try {
                const res = await fetch(`${API_BASE}api/weekly_tips/init`);
                const json = await res.json();
                if (json.countries) {
                    setCountries(json.countries);
                    if (json.countries.length > 0) {
                        setSelectedCountry(json.countries[0]);
                    }
                }
            } catch (err) {
                setErrorMsg("Error loading countries.");
            }
        };
        init();
    }, []);

    // --- Fetch Data on Country Change ---
    useEffect(() => {
        if (!selectedCountry) return;
        fetchTipsData();
    }, [selectedCountry]);

    const fetchTipsData = async () => {
        setLoading(true);
        setSelectedGenTip(null);
        setSelectedAltTip(null);
        try {
            const res = await fetch(`${API_BASE}api/weekly_tips/data?country=${selectedCountry}`);
            const json = await res.json();
            if (json.error) throw new Error(json.error);
            setGeneralTips(json.general_tips || []);
            setAltTips(json.alt_tips || []);
        } catch (err) {
            setErrorMsg("Failed to load tip tables.");
        } finally {
            setLoading(false);
        }
    };

    // --- Action Handlers ---
    const handleDeleteAltTip = async () => {
        if (!selectedAltTip) return;
        if (!window.confirm('Do you really want to delete the selected Tip?')) return;

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}api/weekly_tips/delete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ unique_id: selectedAltTip })
            });
            const json = await res.json();
            if (json.error) alert(json.error);
            else fetchTipsData(); // Refresh tables
        } catch (err) {
            alert("Error deleting tip.");
        } finally {
            setLoading(false);
        }
    };

    const handleARDTip = (actionType: 'A' | 'R' | 'G') => {
        let uniqueId: number | null = null;
        let tipType: 'GeneralTip' | 'AlternateTip' = 'GeneralTip';

        if (actionType === 'G') {
            if (!selectedGenTip) return;
            uniqueId = selectedGenTip;
            tipType = 'GeneralTip';
        } else {
            tipType = 'AlternateTip';
            if (actionType === 'R') {
                if (!selectedAltTip) return;
                uniqueId = selectedAltTip;
            }
        }

        // Set state to open the inline modal component instead of window.open
        setModalAction(actionType);
        setModalTipType(tipType);
        setModalUniqueId(uniqueId);
        setIsModalOpen(true);
    };

    // Callback when the modal component wants to close itself
    const handleModalClose = (shouldRefresh: boolean = false) => {
        setIsModalOpen(false);
        if (shouldRefresh) {
            fetchTipsData(); // Refresh tables if a save was successful
        }
    };

    const handleBack = () => {
        window.location.href = "/SysAdmin";
    };

    const styles = {
        container: { padding: '5px', fontFamily: 'Arial, sans-serif' },
        table: { width: '100%', borderSpacing: '0', padding: '0px' },
        
        // Title banner style
        titleRow: { backgroundColor: '#EDF9CB', color: '#800000', fontWeight: 'bold', fontSize: '16px', textAlign: 'center' as const, padding: '6px' },
        
        // Grid Table styles
        gridWrapper: { overflowY: 'auto' as const, maxHeight: '35vh', border: '1px solid #91aeff', borderTop: 'none', marginBottom: '5px' },
        gridTable: { width: '100%', tableLayout: 'fixed' as const, borderCollapse: 'collapse' as const, fontSize: '12px', fontFamily: 'Arial, Trebuchet MS' },
        
        // Grid Headers
        gridTitleHeader: { backgroundColor: '#c7ddfd', color: '#2859ac', padding: '6px', textAlign: 'center' as const, fontWeight: 'bold', border: '1px solid #91aeff', fontSize: '14px' },
        gridColHeader: { backgroundColor: '#c7ddfd', color: '#2859ac', padding: '6px', textAlign: 'left' as const, fontWeight: 'normal', borderBottom: '1px solid #91aeff', borderRight: '1px solid #a4c2f4', position: 'sticky' as const, top: 0 },
        
        gridTd: { padding: '10px', borderRight: '1px solid #b2c6ff', cursor: 'pointer' },
        
        selectedRow: { backgroundColor: 'orange', color: 'white' },
        altRow: { backgroundColor: '#f4faff' },
        defaultRow: { backgroundColor: 'white' },

        // Standard gray HTML buttons like the screenshot
        buttonRow: { textAlign: 'center' as const, padding: '5px 0' },
        btn: { cursor: 'pointer', width: '95px', margin: '0 5px', padding: '3px', fontFamily: 'Arial, sans-serif', fontSize: '12px', backgroundColor: '#e0e0e0', border: '1px solid #707070', color: 'black' }
    };

    return (
        <div style={styles.container}>
            {loading && <Loading />}
            {errorMsg && <div style={{ color: 'red', fontWeight: 'bold' }}>{errorMsg}</div>}

            {isModalOpen && (
                <EL_AddORModifyTip 
                    tipType={modalTipType} 
                    action={modalAction} 
                    uniqueId={modalUniqueId} 
                    country={selectedCountry} 
                    onClose={handleModalClose} 
                />
            )}

            <table style={styles.table}>
                <tbody>
                    <tr>
                        <td colSpan={2} style={styles.titleRow}>
                            Tip of the Week Manager for: &nbsp;
                            <select 
                                value={selectedCountry} 
                                onChange={(e) => setSelectedCountry(e.target.value)}
                                style={{ fontSize: '14px', fontFamily: 'Arial', cursor: 'pointer' }}
                            >
                                {countries.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </td>
                    </tr>

                    <tr>
                        <td colSpan={2} style={{ padding: '5px 0px 0px 0px' }}>
                            <div style={styles.gridTitleHeader}>General Tip of the week</div>
                            <div style={styles.gridWrapper}>
                                <table style={styles.gridTable}>
<thead>
    <tr>
        <th style={{...styles.gridColHeader, textAlign: 'center', width: '70px'}}>Week No.</th>
        <th style={{...styles.gridColHeader, width: '100px'}}>Week Ending</th>
        <th style={{...styles.gridColHeader, width: '70px'}}>Tips</th>
        <th style={{...styles.gridColHeader, width: '250px'}}>Link Text</th>
        <th style={styles.gridColHeader}>URL</th>  {/* no width set — takes remaining space */}
    </tr>
</thead>
                                    <tbody>
                                        {generalTips.map((row, idx) => {
                                            const isSelected = selectedGenTip === row.UniqueID;
                                            return (
                                                <tr 
                                                    key={row.UniqueID} 
                                                    onClick={() => setSelectedGenTip(row.UniqueID)}
                                                    style={isSelected ? styles.selectedRow : (idx % 2 === 0 ? styles.defaultRow : styles.altRow)}
                                                >
                                                    <td style={{...styles.gridTd, textAlign: 'center'}}>{row.WeekNo}</td>
                                                    <td style={styles.gridTd}>{row.WeekEndDate}</td>
                                                    <td style={styles.gridTd}>{row.GeneralTip}</td>
                                                    <td style={styles.gridTd}>{row.LinkText}</td>
                                                    <td style={styles.gridTd}>{row.URL}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </td>
                    </tr>
                    
                    {/* GENERAL TIP BUTTONS */}
                    <tr>
                        <td colSpan={2} style={styles.buttonRow}>
                            <button 
                                style={{ ...styles.btn, opacity: selectedGenTip === null ? 0.5 : 1 }} 
                                disabled={selectedGenTip === null}
                                onClick={() => handleARDTip('G')}
                            >
                                Modify
                            </button>
                        </td>
                    </tr>

                    {/* ALTERNATIVE TIPS TABLE */}
                    <tr>
                        <td colSpan={2} style={{ padding: '10px 0px 0px 0px' }}>
                            <div style={styles.gridTitleHeader}>Alternative Tip of the week</div>
                            <div style={styles.gridWrapper}>
                                <table style={styles.gridTable}>
                                    <thead>
                                        <tr>
                                            <th style={{...styles.gridColHeader, textAlign: 'center', width: '80px'}}>Week No.</th>
                                            <th style={{...styles.gridColHeader, width: '100px'}}>Week Ending</th>
                                            <th style={{...styles.gridColHeader, width: '100px'}}>Grade / Year</th>
                                            <th style={styles.gridColHeader}>Tips</th>
                                            <th style={styles.gridColHeader}>Link Text</th>
                                            <th style={styles.gridColHeader}>URL</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {altTips.map((row, idx) => {
                                            const isSelected = selectedAltTip === row.UniqueID;
                                            return (
                                                <tr 
                                                    key={row.UniqueID} 
                                                    onClick={() => setSelectedAltTip(row.UniqueID)}
                                                    style={isSelected ? styles.selectedRow : (idx % 2 === 0 ? styles.defaultRow : styles.altRow)}
                                                >
                                                    <td style={{...styles.gridTd, textAlign: 'center'}}>{row.WeekNo}</td>
                                                    <td style={styles.gridTd}>{row.WeekEndDate}</td>
                                                    <td style={styles.gridTd}>{row.GradeYear}</td>
                                                    <td style={styles.gridTd}>{row.AlternateTip}</td>
                                                    <td style={styles.gridTd}>{row.LinkText}</td>
                                                    <td style={styles.gridTd}>{row.URL}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </td>
                    </tr>

                    {/* ALTERNATIVE TIP BUTTONS */}
                    <tr>
                        <td style={{ width: '20%', padding: '5px 0' }}>
                            <button style={styles.btn} onClick={handleBack}>Back</button>
                        </td>
                        <td style={{ width: '80%', textAlign: 'center', padding: '5px 0' }}>
                            <button style={styles.btn} onClick={() => handleARDTip('A')}>Add</button>
                            <button 
                                style={{ ...styles.btn, opacity: selectedAltTip === null ? 0.5 : 1 }} 
                                disabled={selectedAltTip === null}
                                onClick={() => handleARDTip('R')}
                            >
                                Modify
                            </button>
                            <button 
                                style={{ ...styles.btn, opacity: selectedAltTip === null ? 0.5 : 1 }} 
                                disabled={selectedAltTip === null}
                                onClick={handleDeleteAltTip}
                            >
                                Remove
                            </button>
                        </td>
                    </tr>

                </tbody>
            </table>
        </div>
    );
};

export default EL_ManageWeeklyTips;